/**
 * Canonical-JSON + SHA-256 hashing for the working-state ⇄ active-Profile
 * dirty check (ADR-033 §3.2).
 *
 * When a Profile is applied, the working state is set to a copy of the
 * Profile document and ``active_profile.applied_document_sha256`` is set to
 * the hash of the *canonical* serialisation of that document. The UI later
 * recomputes the hash of the current working state and compares: a mismatch
 * means the operator has tweaked the working state away from the applied
 * Profile, and the gear menu shows the "(modified)" marker (REQ-UI-074).
 *
 * Canonicalisation matters: two structurally-equal documents whose object
 * keys were inserted in a different order must hash identically, or every
 * apply→re-read round-trip would falsely report "(modified)". We therefore
 * serialise with **recursively sorted object keys** rather than relying on
 * ``JSON.stringify``'s insertion-order output. Arrays preserve order
 * (order is semantic for ``panels`` / ``clocks`` / ``sections``).
 *
 * The hash uses the Web Crypto ``crypto.subtle`` digest (SHA-256), which is
 * available in every browser the UI targets and in jsdom under Node 20+
 * (the test runtime). No third-party dependency, no network — all
 * client-side per REQ-UI-035.
 *
 * Cross-references:
 *   - REQ-UI-074 (Active + "(modified)" marker); REQ-UI-075 (Apply sets
 *     the hash); ADR-033 §3.2.
 *   - REQ-UI-035 (BFF-only — this is pure client-side compute).
 */

/**
 * Serialise a JSON-compatible value to a canonical string with object keys
 * sorted recursively. Mirrors the BFF's canonical-JSON intent (sorted keys,
 * no incidental whitespace) so a hash computed here matches one the server
 * could compute over the same document. ``undefined`` members are omitted
 * (matching ``JSON.stringify`` semantics) so an optional field left unset
 * does not perturb the hash.
 */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(sortValue(value));
}

/**
 * Recursively rebuild a value with object keys in lexicographic order.
 * Arrays keep their element order; primitives pass through. Returns a new
 * structure — the input is not mutated.
 */
function sortValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortValue);
  }
  if (value !== null && typeof value === "object") {
    const source = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) {
      const member = source[key];
      // Omit ``undefined`` members so an absent optional field hashes the
      // same as one explicitly set to ``undefined`` (JSON.stringify drops
      // both anyway; we drop here too for the sorted rebuild).
      if (member !== undefined) {
        sorted[key] = sortValue(member);
      }
    }
    return sorted;
  }
  return value;
}

/**
 * Compute the SHA-256 hex digest of a value's canonical-JSON serialisation.
 * Async because ``crypto.subtle.digest`` is Promise-based.
 *
 * Used by Apply (to stamp ``applied_document_sha256``) and by the gear
 * menu's "(modified)" check (to compare the current working state against
 * the stamp).
 */
export async function sha256CanonicalJson(value: unknown): Promise<string> {
  const canonical = canonicalJson(value);
  const bytes = new TextEncoder().encode(canonical);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return toHex(new Uint8Array(digest));
}

/** Lower-case hex encoding of a byte array. */
function toHex(bytes: Uint8Array): string {
  let out = "";
  for (const b of bytes) {
    out += b.toString(16).padStart(2, "0");
  }
  return out;
}
